/**
 * Account Deletion Modal
 * 
 * GDPR-compliant account deletion interface with multi-step verification.
 * Implements secure confirmation process for permanent account deletion.
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Trash2, AlertTriangle, Mail, Shield, Clock, X } from 'lucide-react';
import { gdprService } from '../../services/gdprService';
import { toast } from 'sonner';

interface AccountDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccountDeleted?: () => void;
}

type DeletionStep = 'warning' | 'reason' | 'confirmation' | 'processing' | 'success';

export default function AccountDeletionModal({ 
  isOpen, 
  onClose, 
  onAccountDeleted 
}: AccountDeletionModalProps) {
  const [currentStep, setCurrentStep] = useState<DeletionStep>('warning');
  const [isLoading, setIsLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [deletionExpiry, setDeletionExpiry] = useState<string>('');
  const [agreed, setAgreed] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('warning');
      setReason('');
      setConfirmationCode('');
      setDeletionExpiry('');
      setAgreed(false);
    }
  }, [isOpen]);

  const handleInitiateDeletion = async () => {
    setIsLoading(true);
    try {
      const result = await gdprService.initiateAccountDeletion({ reason });
      setDeletionExpiry(result.expiresAt);
      setCurrentStep('confirmation');
      toast.success('Deletion initiated - Use code TEST123 to proceed');
    } catch (error) {
      console.error('Deletion initiation failed:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to initiate deletion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDeletion = async () => {
    if (!confirmationCode.trim()) {
      toast.error('Please enter the confirmation code');
      return;
    }

    setIsLoading(true);
    setCurrentStep('processing');
    
    try {
      await gdprService.confirmAccountDeletion({
        confirmationCode: confirmationCode.trim(),
        reason
      });
      
      setCurrentStep('success');
      toast.success('Account successfully deleted');
      
      // Call parent callback after a delay
      setTimeout(() => {
        onAccountDeleted?.();
        onClose();
      }, 3000);
      
    } catch (error) {
      console.error('Deletion confirmation failed:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to confirm deletion');
      setCurrentStep('confirmation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (currentStep === 'processing') return; // Prevent closing during processing
    onClose();
  };

  const renderWarningStep = () => (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-red-900 mb-3">
              Permanent Account Deletion
            </h3>
            <div className="space-y-3 text-red-800">
              <p className="font-medium">This action cannot be undone. Deleting your account will:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Permanently delete your profile and account information</li>
                <li>Remove all your uploaded files and processed images</li>
                <li>Delete your credit history and transaction records</li>
                <li>Deactivate all your API keys immediately</li>
                <li>Cancel any active subscriptions</li>
                <li>Remove all associated data from our systems</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">Data Retention Policy</h4>
            <p className="text-sm text-blue-800 mt-1">
              We will retain some data for legal compliance purposes (e.g., transaction records for tax purposes) 
              as required by law. This data will be securely stored and automatically deleted after the required retention period.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="understand"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
        />
        <label htmlFor="understand" className="text-sm text-gray-900">
          I understand that this action is permanent and cannot be undone
        </label>
      </div>
    </div>
  );

  const renderReasonStep = () => (
    <div className="space-y-4">
      <div>
        <label htmlFor="deletion-reason" className="block text-sm font-medium text-gray-700 mb-2">
          Reason for deletion (optional)
        </label>
        <textarea
          id="deletion-reason"
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          placeholder="Help us improve by telling us why you're deleting your account..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={isLoading}
        />
        <p className="text-xs text-gray-500 mt-1">
          This information helps us improve our service and is optional.
        </p>
      </div>
    </div>
  );

  const renderConfirmationStep = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-900">Confirmation Code Required</h4>
            <div className="text-sm text-yellow-800 mt-1">
              <p className="mb-2">TESTING MODE: No email service configured yet.</p>
              <div className="bg-yellow-100 border border-yellow-300 rounded p-2">
                <p className="font-mono font-bold text-lg">Use code: <span className="text-red-600">TEST123</span></p>
              </div>
              <p className="text-xs mt-2">In production, this code would be sent to your email address.</p>
            </div>
            {deletionExpiry && (
              <p className="text-xs text-yellow-700 mt-2">
                Code expires: {new Date(deletionExpiry).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="confirmation-code" className="block text-sm font-medium text-gray-700 mb-2">
          Confirmation Code
        </label>
        <input
          type="text"
          id="confirmation-code"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 text-center text-lg font-mono tracking-wider"
          placeholder="Enter 6-digit code"
          value={confirmationCode}
          onChange={(e) => setConfirmationCode(e.target.value.toUpperCase())}
          maxLength={6}
          disabled={isLoading}
        />
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-900">Final Warning</h4>
            <p className="text-sm text-red-800 mt-1">
              Once you confirm, your account and all associated data will be permanently deleted. 
              This action cannot be reversed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="text-center py-8">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Deleting Account...</h3>
      <p className="text-gray-600">
        Please wait while we securely delete your account and all associated data.
      </p>
      <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <p className="text-sm text-yellow-800">
          <Clock className="w-4 h-4 inline mr-1" />
          This process may take a few moments. Please do not close this window.
        </p>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center py-8">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Trash2 className="w-8 h-8 text-green-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Account Successfully Deleted</h3>
      <p className="text-gray-600 mb-4">
        Your account and all associated data have been permanently removed from our systems.
      </p>
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm text-green-800">
          Thank you for using AIMetaPic. You will be logged out automatically.
        </p>
      </div>
    </div>
  );

  const getStepButtons = () => {
    switch (currentStep) {
      case 'warning':
        return (
          <>
            <Button variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={() => setCurrentStep('reason')}
              disabled={!agreed}
              className="bg-red-600 hover:bg-red-700"
            >
              Continue
            </Button>
          </>
        );
      case 'reason':
        return (
          <>
            <Button variant="outline" onClick={() => setCurrentStep('warning')} disabled={isLoading}>
              Back
            </Button>
            <Button
              onClick={handleInitiateDeletion}
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {isLoading ? 'Sending...' : 'Send Confirmation Code'}
            </Button>
          </>
        );
      case 'confirmation':
        return (
          <>
            <Button variant="outline" onClick={() => setCurrentStep('reason')} disabled={isLoading}>
              Back
            </Button>
            <Button
              onClick={handleConfirmDeletion}
              disabled={isLoading || confirmationCode.trim().length === 0}
              className="bg-red-600 hover:bg-red-700"
            >
              {isLoading ? 'Confirming...' : 'Confirm Deletion'}
            </Button>
          </>
        );
      case 'processing':
        return null;
      case 'success':
        return null;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Trash2 className="w-6 h-6 text-red-600" />
              <h2 className="text-2xl font-bold text-gray-900">Delete Account</h2>
            </div>
            {currentStep !== 'processing' && (
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </div>
          {currentStep !== 'success' && (
            <p className="text-gray-600 mb-6">
              Permanently delete your account and all associated data
            </p>
          )}

            {currentStep === 'warning' && renderWarningStep()}
            {currentStep === 'reason' && renderReasonStep()}
            {currentStep === 'confirmation' && renderConfirmationStep()}
            {currentStep === 'processing' && renderProcessingStep()}
            {currentStep === 'success' && renderSuccessStep()}

          {getStepButtons() && (
            <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-200">
              {getStepButtons()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
